import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { getCustomerWorkflowStatus } from "../services/apiService";
import { useParams } from "react-router";

type WorkflowPermission = {
    enabled: boolean;
    reason?: string;
};

export type CustomerWorkflow = {
    login: WorkflowPermission;

    calculation: WorkflowPermission;
    purchaseOrder: WorkflowPermission;
    createPurchaseOrder: WorkflowPermission;
    rentalSchedule: WorkflowPermission;
    createRentalSchedule: WorkflowPermission;
    noa: WorkflowPermission;
    createNOA: WorkflowPermission;
    invoice: WorkflowPermission;

    authorisedUser: WorkflowPermission;
};

type CustomerWorkflowContextType = {
    workflow: CustomerWorkflow;
    menuLoading: boolean;
    refreshWorkflow: () => Promise<void>;
};

const defaultWorkflow: CustomerWorkflow = {
    login: { enabled: true, reason: "" },
    calculation: { enabled: true, reason: "" },
    purchaseOrder: { enabled: false, reason: "Purchase Order is not enabled" },
    createPurchaseOrder: { enabled: false, reason: "Purchase Order is not enabled" },
    rentalSchedule: { enabled: false, reason: "Rental Schedule is not enabled" },
    createRentalSchedule: { enabled: false, reason: "Rental Schedule is not enabled" },
    noa: { enabled: false, reason: "NOA is not enabled" },
    createNOA: { enabled: false, reason: "NOA is not enabled" },
    invoice: { enabled: false, reason: "Invoice is not enabled" },
    authorisedUser: { enabled: false, reason: "Authorised User is not enabled" },
};

const noopRefresh = async () => {};

const fallbackContext: CustomerWorkflowContextType = {
    workflow: defaultWorkflow,
    menuLoading: false,
    refreshWorkflow: noopRefresh,
};

const CustomerWorkflowContext =
    createContext<CustomerWorkflowContextType | undefined>(undefined);

export function CustomerWorkflowProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { customerId } = useParams();
    const [workflow, setWorkflow] = useState<CustomerWorkflow>(defaultWorkflow);
    const [loading, setLoading] = useState(Boolean(customerId));

    const refreshWorkflow = useCallback(async () => {
        if (!customerId) return;
        try {
            setLoading(true);
            const data = await getCustomerWorkflowStatus(customerId);
            setWorkflow(data);
        } catch (error) {
            console.error("Failed to load workflow", error);
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        void refreshWorkflow();
    }, [refreshWorkflow]);

    const value = useMemo(
        () => ({
            workflow,
            menuLoading: loading,
            refreshWorkflow,
        }),
        [workflow, loading, refreshWorkflow]
    );

    return (
        <CustomerWorkflowContext.Provider value={value}>
            {children}
        </CustomerWorkflowContext.Provider>
    );
}

/** Safe outside provider — returns defaults + no-op refresh. */
export function useCustomerWorkflow() {
    return useContext(CustomerWorkflowContext) ?? fallbackContext;
}
